<template>
    <div class="container" :key="build">
        <div :class="{'modal-backdrop': modal, 'hidden': !visible}"> 
            <div :class="{'modal-box': modal}">
                <div :class="{'modal-header': modal, 'hidden': !modal}">
                    <div v-show="!edit" class="modal-name">
                        Add new sub-chart
                    </div>
                    <div v-show="edit" class="modal-name">
                        Edit sub-chart
                    </div>
                    <div class="modal-close-wrapper">
                        <div class="modal-close-btn">
                            <i class="icons8-delete-2" @click="onClose"></i>
                        </div>
                        <div style="clear: both"></div>
                    </div>
                </div>
                
                <div :class="{'modal-body': modal}">
                    <div class="parserBox" :class="{'modal': modal}">
                        <table class="inputTable">
                            <tr>
                                <td>
                                    <div class="tickerSelectBox" v-click-outside="hideTickerTree">
                                        <div class="errorBox"></div>
                                        <div class="tickerInput">
                                            <input type="text" autocomplete="off" v-model="ticker" @input="run" placeholder="Select ticker" @click="tickerOpened = !tickerOpened" ref="ticker" />
                                        </div>
                                        <div class="tickerTree" v-show="tickerOpened && !tickerDisabled">
                                            <tree v-if="isOperator" v-for="node in nodesOperators" :open="true" :key="node.id" :label="node.label" :nodes="node.nodes" :value="node.value" :depth="0" @select="onOperatorSelect" />
                                            <tree v-if="!isOperator" v-for="node in tickerNodesFiltered" :open="tickerNodesOpened" :key="node.id" :label="node.label" :nodes="node.nodes" :value="node.value" :depth="0" @select="onTickerSelect"/>
                                        </div> 
                                    </div>
                                </td>
                                <td>
                                    <div v-click-outside="hideActionTree">
                                        <div class="errorBox"></div>
                                        <div class="actionInput" :class="{'opened': actionOpened, 'closed': !actionOpened}" @click="actionOpened = !actionOpened" >
                                            <div v-if="!actionAny" class="actionValue actionPlaceholder">No available action</div>
                                            <div v-if="actionAny && action == null" class="actionValue actionPlaceholder">Select action</div>
                                            <div v-if="actionAny && action != null" class="actionValue">{{action.name}}</div>
                                        </div>
                                        <div v-show="actionOpened && actionAny" class="actionTree">
                                            <tree v-for="node in actionNodes" :key="node.id" :label="node.label" :nodes="node.nodes" :depth="0" @select="onActionSelect"/> 
                                        </div>
                                    </div>
                                </td>
                                <td :class="{'hidden-td': !hasAlg}">
                                    <transition name="fadeRoll">
                                        <div class="chartInputBox" v-show="hasAlg">
                                            <div class="errorBox"></div>
                                            <div class="algInput" @click="algOpened = !algOpened">
                                                <div class="algValue">{{algLabels[alg]}}</div>
                                            </div>
                                            <div v-show="algOpened" class="algTree">
                                                <tree v-for="node in algNodes" :key="node.id" :label="node.label" :value="node.value" :nodes="node.nodes" :selected="node.selected" :depth="0" @select="onAlgSelect"/> 
                                            </div>
                                        </div>
                                    </transition>
                                </td>
                                <td>
                                    <div class="errorBox"></div>
                                    <div class="chartInputBox">
                                        <input type="button" value="Chart" class="chartInput" :class="{'button-green': chartButtonEnabled, 'button-grey': !chartButtonEnabled}" :disabled="!chartButtonEnabled" @click="onChartClicked"/>
                                    </div>
                                </td>
                            </tr>

                            <tr v-if="hasDatepicker && !hasOffset" :class="{'toolsOut': toolsBoxOut, 'toolsIn': !toolsBoxOut}">
                                <td>
                                    <div class="errorBox"></div>
                                    <div class="chartInputBox">
                                        <date-picker
                                            input-class="dateInput"
                                            popup-class="datePopup"
                                            v-model="time"
                                            :format="dFormat"
                                            :lang="'en'"
                                            :height="35"
                                            :clearable="false"
                                            :disabled="!hasDatepicker"
                                            value-type="timestamp"
                                            ref="datepicker"
                                        >
                                            <i slot="calendar-icon" class="icons8-calendar-11"></i>
                                        </date-picker>
                                    </div>
                                </td>
                                <td></td>
                            </tr>

                            <!-- Modified row for n-years selector -->
                            <tr v-if="hasNYears" :class="{'toolsOut': toolsBoxOut, 'toolsIn': !toolsBoxOut}">
                                <td>
                                    <div class="errorBox">
                                        <span v-show="!n.valid">{{n.error}}</span>
                                    </div>
                                    <div class="chartInputBox">
                                        <select 
                                            class="nYearsSelector"
                                            v-model.number="n.value" 
                                            :class="{'error': !n.valid}" 
                                            @change="validateNYears" 
                                            :disabled="!hasNYears">
                                            <option :value="5">5 years</option>
                                            <option :value="10">10 years</option>
                                            <option :value="15">15 years</option>
                                            <option :value="20">20 years</option>
                                        </select>
                                    </div>
                                </td>
                                <td></td>
                            </tr>

                            <tr v-if="!hasDatepicker && hasOffset" :class="{'toolsOut': toolsBoxOut, 'toolsIn': !toolsBoxOut}">
                                <td>
                                    <div class="errorBox">
                                        <span v-show="!off.valid">{{off.error}}</span>
                                    </div>
                                    <div class="chartInputBox">
                                        <input type="text" autocomplete="off" placeholder="Shift contract window +/- n days" v-model="off.value" :class="{'error': !off.valid}" @input="validateOffset" :disabled="!hasOffset"/>
                                    </div>
                                </td>
                                <td></td>
                            </tr>
                            <tr v-if="hasDatepicker && hasOffset" :class="{'toolsOut': toolsBoxOut, 'toolsIn': !toolsBoxOut}">
                                <td>
                                    <div class="errorBox"></div>
                                    <div class="chartInputBox">
                                        <date-picker
                                            input-class="dateInput"
                                            popup-class="datePopup"
                                            v-model="time"
                                            :format="dFormat"
                                            :lang="'en'"
                                            :height="35"
                                            :clearable="false"
                                            :disabled="!hasDatepicker"
                                            value-type="timestamp"
                                            ref="datepicker"
                                        >
                                            <i slot="calendar-icon" class="icons8-calendar-11"></i>
                                        </date-picker>
                                    </div>
                                </td>
                                <td>
                                    <div class="errorBox">
                                        <span v-show="!off.valid">{{off.error}}</span>
                                    </div>
                                    <div class="chartInputBox">
                                        <input type="text" autocomplete="off" placeholder="Shift contract window +/- n days" v-model="off.value" :class="{'error': !off.valid}" @input="validateOffset" :disabled="!hasOffset"/>
                                    </div>
                                </td>
                            </tr>
                        </table>

                        <div style="float: right; margin-right: 8px;">
                            <div class="errorBox"></div>
                            <input type="button" 
                                value="Tools" 
                                :class="{'button-grey': !hasTools, 'button-blue': hasTools && !toolsBoxOut, 'button-red': toolsBoxOut}" 
                                :disabled="!chartButtonEnabled" 
                                @click="toolsBoxOut = !toolsBoxOut"
                            />
                        </div>
                        <div style="clear: both"></div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import tree from "./tree.vue"; 
    import DatePicker from 'vue2-datepicker';
    import ClickOutside from 'vue-click-outside';

    export default {
        name: 'Parser2',
        components: {"tree": tree, "date-picker": DatePicker},
        props: {
            user: { type: Object, required: true },
            targetId: { type: String, required: true },
            modal: { type: Boolean, default: false },
            visible: { type: Boolean, default: false },
            edit: { type: Boolean, default: false }
        },
        directives: { ClickOutside },
        data() {
            return {
                build: 0,
                operators: { "+": "Add", "-": "Subtract", "*": "Multiply", "/": "Divide" },
                isOperator: false,
                ticker: null,
                tickerOpened: false,
                tickerDisabled: false,
                tickerNodesOpened: false,
                tickerNodesFiltered: [],
                tickerNodesAll: [],
                off: { valid: true, value: null, error: null },
                n: { valid: true, value: 10, error: null },
                nodesOperators: [
                    { label: "- Subtract", value: "-" },
                    { label: "+ Add", value: "+" },
                    { label: "* Multiply", value: "*" },
                    { label: "/ Divide", value: "/" },
                ],
                action: null,
                actionOld: null,
                actions: {
                    price: [
                        { id: "price_volume", base: "Price", name: "Price & volume", enabled: false, functionId: "priceCandlestickFutures" },
                        { id: "price_under", base: "Price", name: "Price & underlyings", enabled: false, functionId: "priceLine", trans: [] },
                        { id: "price_hist", base: "Price", name: "Price", enabled: false, functionId: "priceCandlestickHistorical", trans: ["Aggregate"] },
                        { id: "price_con", base: "Seasonality", name: "Continuous price", enabled: false, functionId: "PriceContinuous", trans: ["PriceContinuous", "Aggregate"] },
                        { id: "histogram", base: "Seasonality", name: "Continuous histogram", enabled: false, functionId: "histogram2", trans: ["PriceContinuous", "Histogram"] },
                        { id: "seasonality_stacked", base: "Seasonality", name: "Seasonality - stacked", enabled: false, functionId: "seasonalityStacked" },
                        { id: "seasonality_average", base: "Seasonality", name: "Seasonality - average", enabled: false, functionId: "seasonalityAverages", trans: ["Average"] },
                        { id: "seasonality_month", base: "Seasonality", name: "Seasonality - by month", enabled: false, functionId: "seasonalityMonth", trans: ["SeasonalityMonth"] },
                        { id: "volume", base: "Price", name: "Volume & OI comparison", enabled: false, functionId: "volume", trans: ["Volume"] }
                    ],
                    ts: [],
                    cot: [],
                },
                actionGroupLabels: { price: "Price", ts: "Term structure", cot: "COT" },
                actionLookup: {
                    futures: ["price_volume", "price_con", "histogram", "seasonality_stacked", "seasonality_average", "seasonality_month"],
                    historical: ["price_hist", "histogram"],
                    spread: ["price_under", "price_con", "histogram", "seasonality_stacked", "seasonality_average", "seasonality_month", "volume"]
                },
                actionAny: false,
                actionOpened: false,
                robj: null,
                time: new Date().getTime(),
                months: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
                dFormat: {
                    stringify: (d) => {
                        return this.months[d.getMonth()] + " " + d.getDate() + ", " + d.getFullYear();
                    }
                },
                alg: "byTS",
                algLabels: {
                    byTS: "Roll method: On Last Trading Day",
                    byOI: "Roll method: By max Open Interest"
                },
                algOpened: false,
                whisperData: [],
                toolsBoxOut: false,
            }
        },
        computed: {
            loggedIn() {
                return this.user.id > 0;
            },
            priceActionsEnabled() { return this.groupEnabled("price") },
            tsActionsEnabled() { return this.groupEnabled("ts") },
            cotActionsEnabled() { return this.groupEnabled("cot") },
            actionNodes() {
                var nodes = [];
                for (var gKey in this.actions) {
                    if (!this.groupEnabled(gKey)) { continue }
                    var node = { id: gKey, label: this.actionGroupLabels[gKey], nodes: [] };
                    for (var i in this.actions[gKey]) {
                        if (!this.actions[gKey][i].enabled) { continue }
                        node.nodes.push({
                            id: this.actions[gKey][i].id,
                            label: this.actions[gKey][i].name,
                            value: this.actions[gKey][i],
                            selected: (this.action !== null && this.action.id == this.actions[gKey][i].id)
                        });
                    }
                    nodes.push(node);
                }
                return nodes;
            },
            algNodes() {
                return [
                    { id: "byTS", label: this.algLabels.byTS, value: "byTS", selected: this.alg == "byTS" },
                    { id: "byOI", label: this.algLabels.byOI, value: "byOI", selected: this.alg == "byOI" }
                ]
            },
            hasDatepicker() {
                return this.action !== null && (this.action.id == "histogram" || this.action.id.substring(0, 2) == "ts");
            },
            hasAlg() {
                if (this.robj === null) { return false }
                return this.action !== null && (this.action.id == "price_hist" || (this.action.id == "histogram" && this.robj.params.dtype == "Historical"));
            },
            hasOffset() {
                if (this.robj === null) { return false }
                return this.action !== null && (this.action.id == "price_con" || (this.action.id == "histogram" && this.robj.params.dtype == "Futures"));
            },
            hasNYears() {
                if (this.robj === null) { return false }
                return this.action !== null && this.action.id == "seasonality_stacked";
            },
            hasOptionalInputs() {
                var has = this.hasDatepicker || this.hasAlg;
                if (!has) { this.algOpened = false }
                return has;
            },
            hasTools() {
                this.toolsBoxOut = this.toolsBoxOut && (this.hasDatepicker || this.hasOffset || this.hasNYears);
                return this.hasDatepicker || this.hasOffset || this.hasNYears;
            },
            chartButtonEnabled() {
                return this.action !== null && this.actionAny && this.loggedIn && this.inputsValid;
            },
            inputsValid() {
                return this.off.valid;
            },
            date() {
                try {
                    var d = new Date(this.time);
                    return d.toISOString().substring(0, 10);
                } catch {
                    var d = new Date();
                    return d.toISOString().substring(0, 10);
                }
            },
        },
        watch: {
            visible: {
                deep: true,
                handler: function (value) { this.$forceUpdate() }
            }
        },
        methods: {
            validateOffset() {
                var minOffset = -178;
                var maxOffset = 178;
                this.off.value = ((this.off.value + "").length == 0) ? null : this.off.value;
                this.off.error = null;
                this.off.valid = true;
                if (this.off.value === null) { return }
                this.off.valid &= /^[\-]?[0-9]{1,}$/.test(this.off.value + "");
                if (!this.off.valid) {
                    this.off.error = "Integers only";
                    return;
                }
                this.off.valid &= this.off.value >= minOffset && this.off.value <= maxOffset;
                if (!this.off.valid) {
                    this.off.error = "Only values between " + minOffset + " and " + maxOffset + " are allowed";
                    return;
                }
            },
            validateNYears() {
                const allowedYears = [5, 10, 15, 20];
                if (allowedYears.includes(this.n.value)) {
                    this.n.valid = true;
                    this.n.error = null;
                } else {
                    this.n.valid = false;
                    this.n.error = "Invalid selection. Please select one of 5, 10, 15, or 20 years.";
                }
            },
            setTickerNodes(data) {
                this.tickerNodesAll = JSON.parse(JSON.stringify(data));
                this.tickerNodesFiltered = JSON.parse(JSON.stringify(data));
            },
            hideTickerTree() { this.tickerOpened = false },
            hideActionTree() { this.actionOpened = false },
            bSplit(s, operator) {
                var bCount = 0;
                var chunk = "";
                var out = [];
                var str = JSON.parse(JSON.stringify(s));
                if (str[0] == "-" && str.search(/^\-[0-9\.]{1,}\*/) == -1 && str.match(/^\-[0-9\.]{1,}$/) == null) {
                    str = "-1*" + str.substring(1, str.length);
                }
                for (var i in str) {
                    if (str[i] == "(") { bCount += 1; }
                    if (str[i] == ")") { bCount -= 1; }
                    if (i == 0 && str[i] == operator) {
                        chunk += str[i];
                        continue;
                    }
                    if (bCount == 0 && str[i] == operator) {
                        out.push(chunk);
                        chunk = "";
                    } else {
                        chunk += str[i];
                    }
                }
                if (chunk.length > 0) { out.push(chunk); }
                return bCount == 0 ? out : [];
            },
            parse(str, dkey) {
                for (var op in this.operators) {
                    var tmp = this.bSplit(str, op);
                    if (tmp.length > 1) { break; }
                }
                if (str[0] == "(" && str[str.length - 1] == ")" && tmp.length == 1) {
                    return this.parse(str.substring(1, str.length - 1), dkey);
                }
                if (tmp.length > 1) {
                    var nodes = {};
                    for (var i in tmp) {
                        nodes[dkey + i] = this.parse(tmp[i], dkey + i);
                    }
                    nodes[dkey] = { dkey: dkey, request: { dkeys: Object.keys(nodes), value: op }, type: "operator" };
                    return nodes;
                }
                var type = tmp[0].match(/^[\-\+]?[0-9\.]{1,}$/) != null ? "scalar" : "data";
                return { dkey: dkey, request: type == "data" ? tmp[0] : parseFloat(tmp[0]), type: type };
            },
            serialize(nodes, baseAction) {
                var sub = [];
                for (var dkey in nodes) {
                    if ("request" in nodes[dkey]) {
                        sub.push({
                            action: (nodes[dkey].type == "operator") ? this.operators[nodes[dkey].request.value] : ((nodes[dkey].type == "data") ? baseAction : "Scalar"),
                            dkey: dkey,
                            request: (nodes[dkey].type == "operator") ? { dkeys: nodes[dkey].request.dkeys } : nodes[dkey].request
                        });
                    } else {
                        sub = sub.concat(this.serialize(nodes[dkey], baseAction));
                    }
                }
                return sub;
            },
            isFutures(str) {
                return str.match(/^[\-\+]?[A-Z0-9]{1,3}[FGHJKMNQUVXZ]{1}[0-9]{2}$/g);
            },
            isHistorical(str) {
                return str.match(/^[\-\+]?[A-Z0-9]{1,3}[0-9]{1,2}$/g);
            },
            getDataType(request) {
                if (this.isFutures(request)) { return "Futures"; }
                if (this.isHistorical(request)) { return "Historical"; }
                return undefined;
            },
            getCommodity(request) {
                if (this.isFutures(request)) { return request.substring(0, request.length - 3); }
                if (this.isHistorical(request)) { return request.substring(0, request.length - 1); }
                return undefined;
            },
            probe(nodes, out) {
                for (var dkey in nodes) {
                    if ("request" in nodes[dkey]) {
                        if (["operator", "scalar"].includes(nodes[dkey].type)) { continue; }
                        var dtype = this.getDataType(nodes[dkey].request);
                        out.dtype = out.dtype === undefined ? dtype : out.dtype;
                        out.valid = out.dtype == dtype;
                        var comm = this.getCommodity(nodes[dkey].request);
                        if (comm !== undefined && !out.comms.includes(comm)) {
                            out.comms.push(comm);
                        }
                        out.dlist.push(nodes[dkey].request);
                    } else {
                        out = this.probe(nodes[dkey], out);
                    }
                }
                return out;
            },
            disableAllActions() {
                for (var gKey in this.actions) {
                    for (var i in this.actions[gKey]) {
                        this.actions[gKey][i].enabled = false;
                    }
                }
            },
            groupEnabled(gKey) {
                for (var i in this.actions[gKey]) {
                    if (this.actions[gKey][i].enabled) { return true; }
                }
                return false;
            },
            actionEnabled(id) {
                for (var gKey in this.actions) {
                    for (var i in this.actions[gKey]) {
                        if (this.actions[gKey][i].id != id) { continue; }
                        return this.actions[gKey][i].enabled;
                    }
                }
                return false;
            },
            isGrainsOnly(comms) {
                for (var i in comms) {
                    if (!["ZC", "ZS", "ZW", "ZL", "ZM", "KE", "ZR", "ZO"].includes(comms[i])) { return false; }
                }
                return true;
            },
            buildActions(pdata) {
                this.disableAllActions();
                this.actions.ts = [];
                this.actions.cot = [];
                if (pdata.dtype === undefined || !pdata.valid) {
                    this.actionAny = false;
                    this.actionOpened = false;
                    return false;
                }
                var atype = (pdata.dtype == "Historical") ? "historical" : ((pdata.dlist.length > 1) ? "spread" : "futures");
                for (var gKey in this.actions) {
                    if (["cot", "ts"].includes(gKey)) { continue; }
                    for (var i in this.actions[gKey]) {
                        this.actions[gKey][i].enabled = this.actionLookup[atype].includes(this.actions[gKey][i].id);
                    }
                }
                for (var i in pdata.comms) {
                    var request = (pdata.comms[i].includes("-") || pdata.comms[i].includes("+")) ? pdata.comms[i].substring(1, pdata.comms[i].length) : pdata.comms[i];
                    this.actions.ts.push({ id: "ts" + request + 1, base: undefined, enabled: true, functionId: "termStructure", name: request + " - Term structure", trans: ["TermStructureHistorical"], request: request });
                    this.actions.cot.push({ id: "cot" + request + 1, base: "Price", enabled: true, name: request + " - Net positions", functionId: "cotNetPositions", trans: ["Cot"], request: request });
                    this.actions.cot.push({ id: "cot" + request + 2, base: "Price", enabled: true, name: request + " - Net positions index", functionId: "cotNetPositionsIndex", trans: ["Cot"], request: request });
                    this.actions.cot.push({ id: "cot" + request + 3, base: "Price", enabled: true, name: request + " - Net traders", functionId: "cotNetTraders", trans: ["Cot"], request: request });
                    this.actions.cot.push({ id: "cot" + request + 4, base: "Price", enabled: true, name: request + " - Net traders index", functionId: "cotNetTradersIndex", trans: ["Cot"], request: request });
                    this.actions.cot.push({ id: "cot" + request + 5, base: "Price", enabled: true, name: request + " - Net concentration %", functionId: "cotNetConcentration", trans: ["Cot"], request: request });
                    this.actions.cot.push({ id: "cot" + request + 6, base: "Price", enabled: true, name: request + " - Net concentration index", functionId: "cotNetConcentrationIndex", trans: ["Cot"], request: request });
                }
                this.actionAny = true;
                this.action = (this.actionOld !== null && this.actionEnabled(this.actionOld.id)) ? this.actionOld : null;
                return true;
            },
            addTransformationChain(chain, pdata) {
                if (this.action.trans === undefined) { return chain; }
                if (this.action.trans[0] == "Volume") {
                    var tmp = [];
                    for (var i in chain) {
                        var link = chain[i];
                        tmp.push(link);
                        if (link.action == "PriceFutures") {
                            link = JSON.parse(JSON.stringify(link));
                            link.action = "Volume";
                            link.request = { dkeys: [link.dkey] };
                            tmp.push(link);
                        }
                    }
                    chain = tmp;
                } else {
                    for (var i in this.action.trans) {
                        var tmp = { action: this.action.trans[i], dkey: "result", request: { dkeys: ["result"] } };
                        if (this.action.request !== undefined) {
                            tmp.request.request = this.action.request;
                        }
                        if (this.action.trans[i] == "Aggregate" && pdata.dlist.length <= 1 && this.action.id != "price_con" && this.action.id != "price_hist") {
                            continue;
                        }
                        if (this.action.trans[i] == "PriceContinuous" && pdata.dtype == "Historical") {
                            continue;
                        }
                        chain.push(tmp);
                    }
                }
                return chain;
            },
            filter(nodes, val) {
                var out = [];
                for (var i in nodes) {
                    if (nodes[i]["nodes"] !== undefined) {
                        nodes[i]["nodes"] = this.filter(nodes[i]["nodes"], val);
                        if (nodes[i]["nodes"].length > 0) {
                            out.push(nodes[i]);
                        }
                    } else {
                        if (nodes[i].value.startsWith(val)) {
                            out.push(nodes[i]);
                        }
                    }
                }
                return out;
            },
            run(e) {
                var cursorPosition = (e === undefined) ? undefined : parseInt(e.target.selectionStart);
                this.robj = null;
                if (this.ticker === null || this.ticker === undefined) { return; }
                var str = this.ticker.replace(/ /g, "");
                str = str.replace(/,/g, ".");
                str = str.toUpperCase();
                if (e !== undefined) {
                    var self = this;
                    (function (e) {
                        self.ticker = str;
                        return new Promise((resolve) => { resolve(e) });
                    })(e).then(function (e) {
                        if (e.target.selectionStart != cursorPosition) {
                            self.$refs.ticker.setSelectionRange(cursorPosition, cursorPosition);
                        }
                    });
                }
                var test = str.split(/[\s\-/+*()]+/).slice(-1)[0];
                this.tickerOpened = true;
                this.isOperator = this.isFutures(test);
                if (!this.isOperator) {
                    this.tickerNodesOpened = test.length > 0;
                    this.tickerNodesFiltered = this.filter(JSON.parse(JSON.stringify(this.tickerNodesAll)), test);
                }
                try {
                    var nodes = { "result": this.parse(str, "result") };
                } catch (err) {
                    this.action = null;
                    this.buildActions({});
                    console.log("Invalid math expression!");
                    return;
                }
                var pdata = this.probe(nodes, { comms: [], dtype: undefined, dlist: [], valid: false });
                pdata.valid &= pdata.comms.length > 0;
                if (!this.buildActions(pdata)) { return; }
                if (!pdata.valid || this.action == null) { return; }
                var chain = (this.action.base !== undefined) ? this.serialize(nodes, this.action.base + pdata.dtype) : [];
                chain = this.addTransformationChain(chain, pdata);
                this.robj = {
                    raw: this.ticker,
                    params: this.getParams(pdata),
                    functionId: this.action.functionId,
                    targetId: this.targetId,
                    chain: chain
                };
                return this.robj;
            },
            getParams(pdata) {
                var n;
                switch (this.action.functionId) {
                    case "histogram2":
                        this.n.value = 70;
                        break;
                    case "PriceContinuous":
                        this.n.value = 70;
                        break;
                    case "seasonalityStacked":
                        this.n.value = (this.n.value !== undefined && this.n.value !== null && this.n.value < 30) ? this.n.value : 10;
                        break;
                    case "seasonalityAverages":
                        this.n.value = 31;
                        break;
                    case "seasonalityMonth":
                        this.n.value = 31;
                        break;
                }
                return {
                    dlist: pdata.dlist,
                    dtype: pdata.dtype,
                    comms: pdata.comms,
                    alg: this.alg,
                    n: this.n.value,
                    time: this.time / 1000,
                    date: this.date,
                    hfield: (pdata.dtype == "Historical") ? "close" : "value0",
                    agg: (this.action.id == "price_con" || pdata.dlist.length > 1) ? "single" : "multi",
                    interpolate: this.action.id == "seasonality_stacked",
                    offset: (this.off.value !== undefined && this.off.value !== null) ? this.off.value : 0
                }
            },
            onChartClicked() {
                if (!this.loggedIn) { return; }
                var robj = this.run();
                if (robj !== undefined) {
                    this.$emit("start", JSON.parse(JSON.stringify(robj)));
                }
            },
            onActionSelect(value) {
                this.action = value;
                this.actionOld = JSON.parse(JSON.stringify(value));
                this.actionOpened = false;
                this.run();
            },
            onAlgSelect(value) {
                this.alg = value;
                this.algOpened = false;
            },
            onTickerSelect(value) {
                this.ticker = (this.ticker === null) ? value : (this.ticker.match(/[a-zA-Z0-9]+$/g) ? this.ticker.replace(/[a-zA-Z0-9]+$/g, value) : this.ticker + value);
                this.isOperator = true;
                this.$refs["ticker"].focus();
                var self = this;
                setTimeout(function () { self.tickerOpened = true; }, 25);
                this.run();
            },
            onOperatorSelect(value) {
                this.isOperator = false;
                this.tickerOpened = false;
                this.tickerNodesFiltered = JSON.parse(JSON.stringify(this.tickerNodesAll));
                this.ticker += value;
                this.$refs["ticker"].focus();
                var self = this;
                setTimeout(function () { self.tickerOpened = true; }, 25);
                this.run();
            },
            onClose() {
                this.$emit("close", this.targetId);
            },
            rollOptional(open) {
                this.$refs.optionalBox.style["max-width"] = (open ? 500 : 0) + "px";
            },
            setTicker(value) {
                this.ticker = value;
            },
            setActionById(actionId) {
                for (var i in this.actions) {
                    for (var j in this.actions[i]) {
                        if (this.actions[i][j].id == actionId) {
                            this.onActionSelect(this.actions[i][j]);
                        }
                    }
                }
            },
            setDate(dstring) {
                var d = new Date(dstring.substring(0, 10) + "T00:00:00Z");
                if (d.getTimezoneOffset() > 0) {
                    d = new Date(d.getTime() + d.getTimezoneOffset() * 60 * 1000);
                }
                this.time = d.getTime();
                this.$forceUpdate();
            },
            setAlg(value) {
                this.onAlgSelect(value);
            }
        },
    }
</script>

<style>
    div.mx-datepicker {
        font-family: Arial;
    }
    @media (min-width: 1080px) {
        div.mx-datepicker { width: 252px; }
    }
    @media (max-width: 1079px) {
        div.mx-datepicker { width: calc((100vw - 115px) * 0.17); }
    }
    span.mx-input-append {
        display: table-cell;
        font-size: 22px;
        padding: 3px 0 0 0;
        color: black;
    }
    input.dateInput {
        border: 1px solid #ddd;
        border-radius: 5px;
        height: 37px;
        width: 100%;
        padding: 0 0 0 10px;
    }
    div.mx-datepicker-popup {
        border-radius: 5px;
        z-index: 2000000000000;
    }
    div.mx-datepicker-popup table,
    div.mx-calendar-header {
        color: black;
        font-family: Arial;
    }
</style>

<style scoped>
    div.parserBox {
        background-color: #d9f2f9;
        padding: 5px 0 12px 0;
        font-family: Arial;
    }
    div.parserBox.modal { background-color: white; }
    table.inputTable { border-collapse: collapse; padding: 0; float: left; }
    table.inputTable > tr > td {
        font-size: 14px;
        padding: 0 0 3px 8px;
    }
    div.tickerInput input,
    div.chartInputBox input[type="text"] {
        height: 35px;
        border: 1px solid #dddddd;
        border-radius: 5px;
        padding: 0 0 0 10px;
    }
    div.actionInput,
    div.algInput {
        background-color: white;
        height: 35px;
        padding: 0 0 0 10px;
        border: 1px solid #dddddd;
        border-radius: 5px;
        cursor: pointer;
    }
    /* Local style for just the NYears selector */
    .nYearsSelector {
        height: 35px;
        border: 1px solid #dddddd;
        border-radius: 5px;
        padding: 0 0 0 10px;
        cursor: pointer;
        font-family: Arial, sans-serif;
        font-size: 14px;
        font-weight: normal;
        color: #000;
        background-color: white;
    }
    @media (min-width: 1080px) {
        .fadeRoll-enter-active,
        .fadeRoll-leave-active,
        .optionalBox {
            opacity: 1.0;
            transition: all 0.2s;
            width: 240px;
        }
        div.tickerInput input,
        div.actionInput,
        div.algInput,
        div.chartInputBox input[type="text"],
        div.chartInputBox select {
            width: 240px;
        }
    }
    @media (max-width: 1079px) {
        .fadeRoll-enter-active,
        .fadeRoll-leave-active,
        .optionalBox {
            opacity: 1.0;
            transition: all 0.2s;
            width: calc((100vw - 150px - 20px) * 0.17);
        }
        div.optionalBox { margin-right: 10px; }
        div.tickerInput input,
        div.actionInput,
        div.algInput,
        div.chartInputBox input[type="text"],
        div.chartInputBox select {
            width: calc((100vw - 150px - 20px) * 0.17);
        }
    }
    .fadeRoll-enter,
    .fadeRoll-leave-to {
        opacity: 0.0;
        transition: all 0.2s;
        width: 0px;
    }
    .transparent { opacity: 0.0; }
    div.actionValue,
    div.algValue {
        margin-top: 10px;
        text-overflow: ellipsis;
        overflow: hidden;
        white-space: nowrap;
    }
    div.actionPlaceholder { color: #777777; }
    div.actionTree,
    div.algTree,
    div.tickerTree {
        position: absolute;
        padding: 12px 0 12px 0px;
        margin: 1px 0 0 0;
        min-width: 250px;
        border: 1px solid #ddd;
        z-index: 1000;
        border-radius: 5px;
        background-color: white;
        box-shadow: rgba(0, 0, 0, 0.176) 0px 6px 12px 0px;
    }
    div.algTree { margin-left: 2px; }
    div.chartInputBox > input,
    div.tickerInput > input { outline: none; }
    div.chartInputBox > input.error { color: red; }
    div.modal-backdrop {
        position: fixed;
        z-index: 1000000;
        width: 100%;
        left: 0;
        right: 0;
        top: 0;
        bottom: 0;
        background-color: rgba(0, 0, 0, 0.75);
        justify-content: center;
        align-items: center;
        display: flex;
    }
    div.modal-box {
        background-color: white;
        box-shadow: 2px 2px 20px 1px;
    }
    div.modal-header {
        height: 40px;
        line-height: 40px;
        background-color: #d9f2f9;
    }
    div.modal-name {
        font-size: 15px;
        font-weight: 700;
        margin-left: 10px;
        float: left;
    }
    div.modal-close-wrapper {
        float: right;
        width: 30px;
        text-align: center;
        cursor: pointer;
    }
    div.modal-body { padding: 10px 10px 10px 10px; }
    div.hidden { display: none; }
    div.optionalBox { }
    div.optionalBox div { max-width: inherit; }
    tr.toolsOut { display: visible; }
    tr.toolsIn { display: none; }
    div.toolsBox {
        border: 1px solid red;
        height: 30px;
    }
    td.hidden-td { padding-left: 0 !important; }
    div.errorBox {
        color: red;
        font-style: italic;
        font-size: 11px;
        height: 13px;
        margin-left: 5px;
    }
</style>

